import React, { useState, useEffect } from 'react';
import '../styles/Modal.css';
import { Mine } from '../pages/HomePage';

interface MineEditProps {
  data: Mine;
  onClose: () => void;
  onSave: (updatedMine: Mine) => void;
  onDelete?: (mineId: string) => void;
}

/**
 * EditModal - A modal component for editing mine information
 * Features form inputs for all mine properties including coordinates, description and links
 */
function MineEdit({ data, onClose, onSave, onDelete }: MineEditProps) {
  const [isClosing, setIsClosing] = useState(false);
  const [formData, setFormData] = useState({
    name: data.title,
    element_type: data.element_type,
    description: data.content,
    latitude: data.position[0],
    longitude: data.position[1],
    links: data.links || [],
    images: data.images || []
  });

  // Element type options
  const elementTypeOptions = [
    { value: 'mine', label: 'Důl' },
    { value: 'pit', label: 'Jáma' },
  ];

  // Handle form input changes
  const handleInputChange = (field: string, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle link changes
  const handleLinkChange = (index: number, field: 'title' | 'url', value: string) => {
    const updatedLinks = [...formData.links];
    updatedLinks[index] = {
      ...updatedLinks[index],
      [field]: value
    };
    setFormData(prev => ({
      ...prev,
      links: updatedLinks
    }));
  };

  // Add new link
  const addLink = () => {
    setFormData(prev => ({
      ...prev,
      links: [
        ...prev.links,
        { id: '', title: '', url: '' }
      ]
    }));
  };

  // Remove link
  const removeLink = (index: number) => {
    setFormData(prev => ({
      ...prev,
      links: prev.links.filter((_, i) => i !== index)
    }));
  };

  // Handle image changes (URL and comment)
  const handleImageChange = (index: number, field: 'src' | 'comment', value: string) => {
    const updatedImages = [...formData.images];
    updatedImages[index] = {
      ...updatedImages[index],
      [field]: value
    };
    setFormData(prev => ({
      ...prev,
      images: updatedImages
    }));
  };

  // Add new image
  const addImage = () => {
    setFormData(prev => ({
      ...prev,
      images: [
        ...prev.images,
        { id: crypto.randomUUID(), src: '', comment: '' }
      ]
    }));
  };

  // Remove image
  const removeImage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  // Handle modal closing with animation
  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 300); // Match CSS animation duration
  };

  // Handle form submission
  const handleSave = () => {
    const updatedMine: Mine = {
      ...data,
      title: formData.name,
      element_type: formData.element_type,
      content: formData.description,
      position: [formData.latitude, formData.longitude],
      links: formData.links.filter(link => link.title.trim() && link.url.trim()),
      images: formData.images.filter(image => image.src.trim() && image.comment.trim())
    };
    onSave(updatedMine);
    handleClose();
  };

  // Handle delete
  const handleDelete = () => {
    if (onDelete && window.confirm('Opravdu chcete smazat tento důl? Tato akce je nevratná.')) {
      onDelete(data.id);
      onClose();
    }
  };

  // Close modal when clicking outside the modal content
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className={`modal edit-modal ${isClosing ? 'closing' : ''}`}>
        {/* Header with title and close button */}
        <div className="modal-header">
          <h2 className="modal-title">Upravit prvek</h2>
          <div style={{ display: 'flex', gap: '8px' }}>
            {onDelete && (
              <button 
                className="btn btn-danger" 
                onClick={handleDelete}
                title="Smazat důl"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: '14px', height: '14px' }}>
                  <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                </svg>
                Smazat
              </button>
            )}
            <button className="close-btn" onClick={handleClose}>
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Form content */}
        <div className="modal-body">
          <div className="modal-content">
            {/* Mine name */}
            <div className="modal-form-group">
              <label className="modal-form-label">Název prvku</label>
              <input
                type="text"
                className="modal-form-input"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Zadejte název dolu"
              />
            </div>

            {/* Element type */}
            <div className="modal-form-group">
              <label className="modal-form-label">Typ prvku</label>
              <select
                className="modal-form-input"
                value={formData.element_type}
                onChange={(e) => handleInputChange('element_type', e.target.value)}
              >
                {elementTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Coordinates */}
            <div className="form-row">
              <div className="modal-form-group">
                <label className="modal-form-label">Zeměpisná šířka</label>
                <input
                  type="number"
                  step="0.000001"
                  className="modal-form-input"
                  value={formData.latitude}
                  onChange={(e) => handleInputChange('latitude', parseFloat(e.target.value))}
                  placeholder="50.123456"
                />
              </div>
              <div className="modal-form-group">
                <label className="modal-form-label">Zeměpisná délka</label>
                <input
                  type="number"
                  step="0.000001"
                  className="modal-form-input"
                  value={formData.longitude}
                  onChange={(e) => handleInputChange('longitude', parseFloat(e.target.value))}
                  placeholder="14.123456"
                />
              </div>
            </div>

            {/* Description */}
            <div className="modal-form-group">
              <label className="modal-form-label">Popis prvku</label>
              <textarea
                className="modal-form-textarea"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Zadejte popis dolu..."
                rows={3}
              />
            </div>

            {/* Images section */}
            <div className="modal-form-group">
              <div className="links-header">
                <label className="modal-form-label">Obrázky</label>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={addImage}
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: '14px', height: '14px' }}>
                    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                  </svg>
                  Přidat obrázek
                </button>
              </div>
              
              {formData.images.map((image, index) => (
                <div key={image.id} className="image-row">
                  <div className="modal-form-group">
                    <label className="modal-form-label">Komentář</label>
                    <input
                      type="text"
                      className="modal-form-input"
                      value={image.comment}
                      onChange={(e) => handleImageChange(index, 'comment', e.target.value)}
                      placeholder="Popis obrázku"
                    />
                  </div>
                  <div className="modal-form-group">
                    <label className="modal-form-label">URL obrázku</label>
                    <input
                      type="url"
                      className="modal-form-input"
                      value={image.src}
                      onChange={(e) => handleImageChange(index, 'src', e.target.value)}
                      placeholder="https://example.com/image.jpg"
                    />
                  </div>
                  <button
                    type="button"
                    className="remove-image-btn"
                    onClick={() => removeImage(index)}
                    title="Odstranit obrázek"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                    </svg>
                  </button>
                </div>
              ))}
            </div>

            {/* Links section */}
            <div className="modal-form-group">
              <div className="links-header">
                <label className="modal-form-label">Užitečné odkazy</label>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={addLink}
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: '14px', height: '14px' }}>
                    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                  </svg>
                  Přidat odkaz
                </button>
              </div>
              
              {formData.links.map((link, index) => (
                <div key={index} className="link-row">
                  <div className="modal-form-group">
                    <label className="modal-form-label">Text odkazu</label>
                    <input
                      type="text"
                      className="modal-form-input"
                      value={link.title}
                      onChange={(e) => handleLinkChange(index, 'title', e.target.value)}
                      placeholder="Název odkazu"
                    />
                  </div>
                  <div className="modal-form-group">
                    <label className="modal-form-label">URL adresa</label>
                    <input
                      type="url"
                      className="modal-form-input"
                      value={link.url}
                      onChange={(e) => handleLinkChange(index, 'url', e.target.value)}
                      placeholder="https://example.com"
                    />
                  </div>
                  <button
                    type="button"
                    className="remove-link-btn"
                    onClick={() => removeLink(index)}
                    title="Odstranit odkaz"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={handleClose}>
            Zrušit
          </button>
          <button className="btn btn-primary" onClick={handleSave}>
            <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: '14px', height: '14px' }}>
              <path d="M17 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.11 0 2-.9 2-2V7l-4-4zm-5 16c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm3-10H5V5h10v4z"/>
            </svg>
            Uložit
          </button>
        </div>
      </div>
    </div>
  );
}

export default MineEdit;
